<template>
  <v-row justify="center">
    <v-dialog
      origin="top center"
      v-model="customerDialog"
      :max-width="dialogWidth"
      content-class="customer-dialog"
      persistent
    >
      <v-card>
        <!-- Header -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <div class="d-flex align-center">
            <v-avatar size="38" color="cyan lighten-5" class="mr-3">
              <v-icon color="cyan darken-2"
                >mdi-account-box-multiple-outline</v-icon
              >
            </v-avatar>
            <div class="dialog-title color-custom-blue">Select Customer</div>
          </div>
          <v-btn
            icon
            small
            class="custom-grey-border bg-slate-50"
            v-on:click="$emit('resetAll', true)"
          >
            <v-icon color="slate-600" small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Search & Action Toolbar -->
          <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-1 mb-3">
            <v-row dense align="center">
              <v-col cols="12" sm="12" md="12" class="py-0">
                <label
                  for="search-customer-input"
                  class="font-size-13 font-weight-600 mb-0"
                >
                  Which customer would you like to create this for?
                </label>
              </v-col>
              <v-col cols="12" sm="7" md="7">
                <v-text-field
                  id="search-customer-input"
                  ref="search-customer"
                  v-model.trim="customer"
                  :loading="searchLoader"
                  :placeholder="placeholder"
                  autocomplete="off"
                  dense
                  filled
                  solo
                  flat
                  hide-details
                  color="cyan"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  @blur="isFocused = false"
                  @focus="isFocused = true"
                  @keydown.esc="onEsc"
                  @keydown.enter="onEnter"
                  @keydown.tab="onTab"
                  class="custom-erp-input bg-white rounded-lg"
                />
              </v-col>
              <v-col cols="12" sm="5" md="5" class="d-flex align-center gap-2">
                <v-btn
                  v-on:click="searchCustomer()"
                  :loading="searchLoader"
                  :disabled="searchLoader"
                  depressed
                  color="cyan darken-2 white--text"
                  class="custom-bold-button mr-3"
                  height="36"
                >
                  <v-icon small left>mdi-magnify</v-icon>
                  Search
                </v-btn>

                <v-btn
                  v-if="getPermission('customer:create') && false"
                  depressed
                  color="custom-btn-color white--text"
                  class="custom-bold-button"
                  height="36"
                  v-on:click="createCustomerDialog = true"
                >
                  <v-icon small left>mdi-plus</v-icon>
                  Create Customer
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

          <!-- Customer List Container -->
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll pr-1"
            style="max-height: calc(100vh - 320px); position: relative"
          >
            <div v-if="customerList && customerList.length">
              <v-card
                v-for="(data, index) in customerList"
                :key="`parent-${index}`"
                flat
                outlined
                class="rounded-lg px-3 py-0 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
                v-on:click="selectCustomer(data)"
              >
                <v-row dense align="center">
                  <!-- Left: Customer Info -->
                  <v-col cols="12" md="8" class="py-1">
                    <div class="d-flex align-center flex-wrap gap-2 mb-1">
                      <Barcode :barcode="data.barcode" />
                      <v-chip
                        color="blue white--text"
                        label
                        small
                        class="ml-1"
                        style="height: 22px"
                        v-if="data.company_type == 'company'"
                      >
                        <span class="font-size-12 font-weight-500">
                          Company
                        </span>
                      </v-chip>
                      <v-chip
                        color="red white--text"
                        label
                        small
                        class="ml-1"
                        style="height: 22px"
                        v-if="data.company_type == 'personal'"
                      >
                        <span class="font-size-12 font-weight-500">
                          Individual
                        </span>
                      </v-chip>
                    </div>

                    <div
                      class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                    >
                      <v-icon small left color="slate-600">mdi-domain</v-icon>
                      <template v-if="data.display_name">
                        {{ data.display_name }}
                      </template>
                      <em v-else class="text-muted font-italic font-weight-400"
                        >no customer name</em
                      >
                    </div>

                    <div
                      class="d-flex align-center flex-wrap font-size-12 text-slate-700 gap-3"
                    >
                      <span
                        v-if="getDefaultPerson(data.default_person)"
                        class="d-flex align-center mr-3"
                      >
                        <v-icon small left color="slate-500"
                          >mdi-account-circle-outline</v-icon
                        >
                        <span class="font-weight-600 text-muted mr-1"
                          >Contact:</span
                        >
                        <span class="font-weight-500 text-slate-900">{{
                          getDefaultPerson(data.default_person)
                        }}</span>
                      </span>

                      <span
                        v-if="data.single_billing"
                        class="d-flex align-center font-weight-500 custom-nowrap-ellipsis"
                      >
                        <v-icon small left color="slate-500"
                          >mdi-map-marker-outline</v-icon
                        >
                        {{ data.single_billing.street_1 }}
                        {{ data.single_billing.street_2 }}
                        {{ data.single_billing.zip_code }}
                      </span>
                    </div>
                  </v-col>

                  <!-- Right: Select CTA & Counts -->
                  <v-col
                    cols="12"
                    md="4"
                    class="text-md-right d-flex flex-column align-md-end justify-center py-1"
                  >
                    <div class="d-flex align-center gap-2 mb-1">
                      <v-chip
                        x-small
                        color="slate-100"
                        class="font-weight-600 text-slate-700"
                      >
                        {{ data.property_count }} Site(s)
                      </v-chip>
                      <v-chip
                        x-small
                        color="slate-100"
                        class="font-weight-600 text-slate-700 ml-1"
                      >
                        {{ data.persons_count }} Person(s)
                      </v-chip>
                    </div>

                    <v-btn
                      small
                      depressed
                      color="cyan lighten-5"
                      class="custom-bold-button px-3 cyan--text text-darken-2"
                    >
                      Select Customer
                      <v-icon small right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </div>

            <!-- Empty State -->
            <div
              v-else
              class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
            >
              <v-avatar size="56" color="slate-100" class="mb-3">
                <v-icon color="slate-400" large>mdi-account-off-outline</v-icon>
              </v-avatar>
              <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
                No Customers Found
              </div>
              <div class="caption-1 text-muted max-w-300 mb-3">
                There are no matching customers in the database.
              </div>
              <v-btn
                v-if="getPermission('customer:create') && false"
                depressed
                color="custom-btn-color white--text"
                class="custom-bold-button"
                height="32"
                v-on:click="createCustomerDialog = true"
              >
                <v-icon small left>mdi-plus</v-icon>
                Create Customer
              </v-btn>
            </div>
          </perfect-scrollbar>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Dialog
      v-if="createCustomerDialog"
      :commonDialog="createCustomerDialog"
      :dialogWidth="1024"
    >
      <template v-slot:title>Create Customer</template>
      <template v-slot:body>
        <v-form
          ref="createCustomerOnly"
          v-model.trim="formValid"
          lazy-validation
          v-on:submit.stop.prevent="createCustomerOnly"
        >
          <DialogContactDetails
            :isDialog="true"
            v-on:saveContactDetail="updateCustomerDetailOnly"
          >
          </DialogContactDetails>
        </v-form>
      </template>
      <template v-slot:action>
        <v-btn
          :disabled="!formValid || formLoading"
          :loading="formLoading"
          class="mx-2 custom-bold-button white--text"
          color="cyan"
          v-on:click="createCustomerOnly"
          >Save Customer
        </v-btn>
        <v-btn
          :disabled="formLoading"
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="createCustomerDialog = false"
          >Close
        </v-btn>
      </template>
    </Dialog>
  </v-row>
</template>

<style scoped>
.v-dialog__content {
  align-items: start;
}
</style>

<script>
import CustomerMixin from "@/core/plugins/customer-mixin";
import Dialog from "@/view/pages/partials/Dialog.vue";
import { POST, CLEAR_ERROR } from "@/core/services/store/request.module";
import DialogContactDetails from "@/view/pages/customer/create-or-update/Contact-Details.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";

export default {
  mixins: [CustomerMixin],
  data() {
    return {
      formValid: true,
      formLoading: false,
      isFocused: false,
      searchLoader: true,
      customer: null,
      timeoutLimit: 500,
      timeout: null,
      customerArr: new Object(),
      createCustomerDialog: false,
      customerList: new Array(),
    };
  },
  props: {
    customerDialog: {
      type: Boolean,
      required: true,
      default: false,
    },
    dialogWidth: {
      type: Number,
      default: 850,
    },
    entity: {
      type: Number,
      default: 0,
    },
    isProject: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Dialog,
    Barcode,
    DialogContactDetails,
  },
  watch: {
    customer(param) {
      if (!param) {
        const _this = this;
        clearTimeout(_this.timeout);
        _this.timeout = setTimeout(function () {
          _this.searchCustomer();
        }, _this.timeoutLimit);
      }
    },
  },
  methods: {
    onEsc() {
      this.resetSearch();
    },
    onEnter() {
      if (this.isSearching) {
        this.searchCustomer();
      }
    },
    onTab() {
      if (this.isSearching) {
        this.searchCustomer();
      }
    },
    resetSearch() {
      const _this = this;
      _this.$nextTick(() => {
        _this.customer = null;
        _this.isFocused = false;
      });
    },
    searchCustomer() {
      const _this = this;
      _this.searchLoader = true;
      _this
        .getCustomers(_this.customer, _this.entity)
        .then((response) => {
          _this.customerList = response;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.searchLoader = false;
          _this.createCustomerDialog = false;
        });
    },
    selectCustomer(param) {
      if (
        param.persons_count === 1 &&
        this.lodash.isEmpty(param.single_person) === false
      ) {
        this.$emit("selectCustomerPerson", param.single_person.id);
      }
      if (
        param.property_count === 1 &&
        this.lodash.isEmpty(param.single_property) === false
      ) {
        this.$emit("selectCustomerProperty", param.single_property.id);
      }
      if (
        param.billing_count === 1 &&
        this.lodash.isEmpty(param.single_billing) === false
      ) {
        this.$emit("selectCustomerBilling", param.single_billing.id);
      }
      if (this.isProject) {
        this.$emit("selectCustomer", param);
      } else {
        this.$emit("selectCustomer", param.id);
      }
    },
    getProfileImage(imagePath) {
      if (!imagePath) {
        return this.$filePath + "storage/uploads/images/no-image.png";
      }
      return this.$filePath + imagePath;
    },
    getDefaultPerson(defaultPerson) {
      if (this.lodash.isEmpty(defaultPerson)) {
        return "";
      }
      return defaultPerson.display_name;
    },
    updateCustomerDetailOnly(param) {
      this.customerArr = param;
    },
    createCustomerOnly() {
      const _this = this;
      if (!_this.$refs.createCustomerOnly.validate()) {
        return false;
      }

      _this.formLoading = true;
      _this.$store.dispatch(CLEAR_ERROR, {});

      const formData = {
        title: _this.customerArr.salutation,
        display_name: _this.customerArr.display_name,
        company_name: _this.customerArr.company_name,
        profile_logo: _this.customerArr.profile_logo,
        first_name: _this.customerArr.first_name,
        last_name: _this.customerArr.last_name,
        group_id: _this.customerArr.group_id,
        primary: +_this.customerArr.primary,
        details: _this.customerArr.details,
      };

      _this.$store
        .dispatch(POST, { url: "customer-only", data: formData })
        .then(() => {
          _this.searchCustomer();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
  },
  mounted() {
    this.searchCustomer();
    this.$refs["search-customer"].focus();
  },
  computed: {
    isSearching() {
      return this.customer && this.customer.length > 0;
    },
    placeholder() {
      return this.isFocused
        ? "Start Typing then press enter"
        : "Search Customer...";
    },
    getDefaultImage() {
      return this.$filePath + "storage/uploads/images/no-image.png";
    },
  },
};
</script>
